export type Volume = {
  numeral: string;
  title: string;
  tagline: string;
  intro: string[];
  inside: string[];
  takeaway: string;
  note?: string;
  coverX: number;
  coverY: number;
};

export const volumes: Volume[] = [
  {
    numeral: "I",
    title: "The Hemodynamic Monitoring Foundations Guide",
    tagline: "Build a stronger conceptual foundation before exploring advanced monitoring.",
    intro: [
      "Hemodynamic monitoring becomes easier to study when you understand the cardiovascular principles behind the measurements.",
      "This first volume organizes essential physiology, terminology, and monitoring concepts into one educational reference.",
    ],
    inside: [
      "Fundamentals of cardiovascular physiology",
      "Cardiac cycle and circulation concepts",
      "Blood pressure and blood flow fundamentals",
      "Introduction to cardiac output",
      "Stroke volume and heart rate relationships",
      "Preload, afterload, and contractility definitions",
      "Basic principles of tissue perfusion",
      "Introduction to hemodynamic monitoring",
      "Understanding trends versus isolated measurements",
      "Educational diagrams and review exercises",
    ],
    takeaway:
      "A structured foundation for understanding essential cardiovascular physiology and the concepts behind hemodynamic monitoring.",
    coverX: 53.3,
    coverY: 4.6,
  },
  {
    numeral: "II",
    title: "The Hemodynamic Parameters Reference Atlas",
    tagline: "Understand the meaning behind the numbers.",
    intro: [
      "Hemodynamic monitoring involves multiple related measurements. Studying these parameters individually can make it difficult to understand their broader physiological context.",
      "This volume brings essential monitoring concepts together through organized explanations and visual comparisons.",
    ],
    inside: [
      "Mean arterial pressure (MAP) fundamentals",
      "Cardiac output (CO)",
      "Cardiac index (CI)",
      "Stroke volume (SV)",
      "Stroke volume index (SVI)",
      "Central venous pressure (CVP)",
      "Systemic vascular resistance (SVR)",
      "Pulmonary artery pressure concepts",
      "Selected advanced hemodynamic parameters",
      "Measurement limitations and variability",
    ],
    takeaway:
      "A visual educational reference for comparing selected hemodynamic parameters, understanding what they represent, and recognizing their limitations.",
    coverX: 76.2,
    coverY: 4.6,
  },
  {
    numeral: "III",
    title: "The Cardiac Performance & Circulatory Physiology Blueprint",
    tagline: "Connect preload, afterload, contractility, and cardiac performance.",
    intro: [
      "These concepts are central to cardiovascular physiology, yet they can be difficult to organize when studied through disconnected definitions.",
      "This volume explains their relationships through diagrams, comparisons, and educational examples.",
    ],
    inside: [
      "Preload and ventricular filling concepts",
      "Afterload and vascular resistance",
      "Myocardial contractility fundamentals",
      "Frank-Starling mechanism",
      "Cardiac output relationships",
      "Ventricular performance concepts",
      "Circulatory pressure-flow relationships",
      "Physiological factors affecting cardiac performance",
      "Limitations of simplified cardiovascular models",
      "Guided physiology review exercises",
    ],
    takeaway:
      "A structured framework for studying how cardiovascular physiology relates to hemodynamic measurements.",
    coverX: 99,
    coverY: 4.6,
  },
  {
    numeral: "IV",
    title: "The Advanced Hemodynamic Monitoring Atlas",
    tagline: "Explore advanced monitoring concepts through clear educational references.",
    intro: [
      "Advanced hemodynamic monitoring introduces additional measurements, technologies, and interpretation considerations.",
      "This volume helps you organize the terminology and principles behind selected monitoring approaches.",
    ],
    inside: [
      "Invasive versus noninvasive monitoring concepts",
      "Arterial pressure monitoring fundamentals",
      "Arterial waveform terminology",
      "Pulmonary artery catheter concepts",
      "Thermodilution principles",
      "Pulse contour analysis fundamentals",
      "Dynamic monitoring parameters",
      "Stroke volume variation concepts",
      "Pulse pressure variation concepts",
      "Common sources of measurement error and limitations",
    ],
    takeaway:
      "An organized educational introduction to advanced hemodynamic monitoring technologies, terminology, and measurement considerations.",
    coverX: 52.8,
    coverY: 43.5,
  },
  {
    numeral: "V",
    title: "The Tissue Perfusion & Hemodynamic Trends Reference",
    tagline: "Study why individual measurements never tell the whole story.",
    intro: [
      "Hemodynamic parameters provide information about circulation, but interpreting their significance requires an understanding of physiology, measurement limitations, and broader clinical context.",
      "This volume focuses on organizing concepts related to tissue perfusion and monitoring trends.",
    ],
    inside: [
      "Fundamentals of tissue perfusion",
      "Oxygen delivery and consumption concepts",
      "Perfusion-related monitoring terminology",
      "Lactate physiology and interpretation limitations",
      "Peripheral perfusion assessment concepts",
      "Hemodynamic trends over time",
      "Measurement variability",
      "Factors influencing cardiovascular measurements",
      "Recognizing uncertainty and incomplete information",
      "Educational trend-review exercises",
    ],
    takeaway:
      "A structured educational reference for understanding perfusion-related concepts and studying hemodynamic trends without relying on isolated values.",
    coverX: 75.6,
    coverY: 43.5,
  },
  {
    numeral: "VI",
    title: "The Hemodynamic Clinical Casebook",
    tagline: "Move beyond memorization and practice organizing clinical information.",
    intro: [
      "One of the most useful ways to study hemodynamic monitoring is to work through simulated scenarios.",
      "This volume brings together concepts from the previous five volumes through guided educational cases.",
    ],
    inside: [
      "Simulated cardiovascular monitoring scenarios",
      "Cardiac output and blood pressure exercises",
      "Preload and afterload concept cases",
      "Hemodynamic trend-review scenarios",
      "Tissue perfusion educational cases",
      "Cases involving conflicting measurements",
      "Examples featuring incomplete information",
      "Measurement limitation exercises",
      "Guided clinical reasoning worksheets",
      "Suggested educational explanations and self-assessment questions",
    ],
    takeaway:
      "An opportunity to practice organizing hemodynamic information, identifying uncertainties, and explaining your reasoning through simulated educational cases.",
    note: "These cases are educational exercises, not validated clinical decision algorithms or substitutes for supervised clinical practice.",
    coverX: 99,
    coverY: 43.5,
  },
];
